import { addDays } from 'date-fns/addDays';
import { addMonths } from 'date-fns/addMonths';
import { addWeeks } from 'date-fns/addWeeks';
import { addYears } from 'date-fns/addYears';
import { differenceInDays } from 'date-fns/differenceInDays';
import { differenceInMonths } from 'date-fns/differenceInMonths';
import { differenceInWeeks } from 'date-fns/differenceInWeeks';
import { differenceInYears } from 'date-fns/differenceInYears';
import { endOfMonth } from 'date-fns/endOfMonth';
import { endOfQuarter } from 'date-fns/endOfQuarter';
import { endOfWeek } from 'date-fns/endOfWeek';
import { endOfYear } from 'date-fns/endOfYear';
import { format as formatFn } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';
import { startOfMonth } from 'date-fns/startOfMonth';
import { startOfQuarter } from 'date-fns/startOfQuarter';
import { startOfWeek } from 'date-fns/startOfWeek';
import { startOfYear } from 'date-fns/startOfYear';
import { subDays } from 'date-fns/subDays';
import { subMonths } from 'date-fns/subMonths';
import { subWeeks } from 'date-fns/subWeeks';
import { subYears } from 'date-fns/subYears';

import { serializeDate } from '@/lib/dateFormatter';

import { applyTemporalBoundary, applyTemporalUnit, diffTemporalUnit } from './temporal';
import type { TaskDateAddUnit, TaskDateBoundaryUnit, TaskUtils } from './types';
import { caughtErrorDetail, warnSdkUtilFallback } from './warn';

const addDateUnits = {
  days: addDays,
  weeks: addWeeks,
  months: addMonths,
  years: addYears
} satisfies Record<TaskDateAddUnit, (date: Date, amount: number) => Date>;

const subtractDateUnits = {
  days: subDays,
  weeks: subWeeks,
  months: subMonths,
  years: subYears
} satisfies Record<TaskDateAddUnit, (date: Date, amount: number) => Date>;

const diffDateUnits = {
  days: differenceInDays,
  weeks: differenceInWeeks,
  months: differenceInMonths,
  years: differenceInYears
} satisfies Record<TaskDateAddUnit, (end: Date, start: Date) => number>;

const startOfDateUnits = {
  week: (date: Date) => startOfWeek(date, { weekStartsOn: 1 }),
  month: startOfMonth,
  quarter: startOfQuarter,
  year: startOfYear
} satisfies Record<TaskDateBoundaryUnit, (date: Date) => Date>;

const endOfDateUnits = {
  week: (date: Date) => endOfWeek(date, { weekStartsOn: 1 }),
  month: endOfMonth,
  quarter: endOfQuarter,
  year: endOfYear
} satisfies Record<TaskDateBoundaryUnit, (date: Date) => Date>;

export function addDate(
  value: string | null | undefined,
  amount: number,
  unit: TaskDateAddUnit
): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const result = applyTemporalUnit(parseISO(value), amount, unit, addDateUnits);
  return serializeDate(result) as string;
}

export function subtractDate(
  value: string | null | undefined,
  amount: number,
  unit: TaskDateAddUnit
): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const result = applyTemporalUnit(parseISO(value), amount, unit, subtractDateUnits);
  return serializeDate(result) as string;
}

export function diffDate(
  a: string | null | undefined,
  b: string | null | undefined,
  unit: TaskDateAddUnit = 'days'
): number | null {
  if (a === null || a === undefined || a === '' || b === null || b === undefined || b === '') {
    return null;
  }

  const start = parseISO(a);
  const end = parseISO(b);

  return diffTemporalUnit(start, end, unit, diffDateUnits);
}

export function startOfDate(
  value: string | null | undefined,
  unit: TaskDateBoundaryUnit
): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const result = applyTemporalBoundary(parseISO(value), unit, startOfDateUnits);
  return serializeDate(result) as string;
}

export function endOfDate(
  value: string | null | undefined,
  unit: TaskDateBoundaryUnit
): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const result = applyTemporalBoundary(parseISO(value), unit, endOfDateUnits);
  return serializeDate(result) as string;
}

export function formatDate(
  value: string | null | undefined,
  format: string = 'yyyy-MM-dd'
): string | null {
  if (!value) {
    warnSdkUtilFallback('date.format', 'No date was provided; returning null.');
    return null;
  }

  try {
    return formatFn(parseISO(value), format);
  } catch (error) {
    warnSdkUtilFallback(
      'date.format',
      `The date could not be formatted: ${caughtErrorDetail(error)}. Returning null.`
    );
    return null;
  }
}

export function currentDate(): string {
  return formatFn(new Date(), 'yyyy-MM-dd');
}

export const dateUtils: TaskUtils['date'] = {
  add: addDate,
  subtract: subtractDate,
  diff: diffDate,
  startOf: startOfDate,
  endOf: endOfDate,
  format: formatDate,
  current: currentDate
};
